import React, { useState, useEffect, useRef } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// --- IMPORTS ---
import Layout from "../../../../Layout/Layout";
import CustomerConstants from "../../../../common/CustomerConstants";
// eslint-disable-next-line
import Loader from "../../../../Components/Loader";
import { SearchField } from "../../../../Components/formElements/SearchField";
import { TableStatusField } from "../../../../Components/formElements/TableStatusField";
import { formatDate, formatDateWithAMPM } from "../../../../common/Validations";
import {
  clearEquipmentRentNumbers,
  getEquipmentRentExtist,
  setEquipmentRentExtDocumentNumber,
} from "../../../../redux/feature/customer_app/customerSlice";
import MultiColumnFilter from "../../../../Components/MultiColumnFilter/MultiColumnFilter";
import CancelModal from "./ERFCancelModel";
import ShowStatus from "../../../../Components/Modal/supplier_app/Workflow/ShowStatus";
import CommonItemLevelDatatableModal from "../../../../Components/Modal/supplier_app/CommonItemLevelDatatableModal";
import { useReactToPrint } from "react-to-print";
import ErfPrintout from "./ErfPrintout";
import EarlyClosureFormPrintout from "./EarlyClosureFormPrintout";
import ClosureRefundFormPrintout from "./ClosureRefundFormPrintout";
import DeliveryChallanPrintout from "./DeliveryChallanPrintout";
import ReceiptPrintOut from "./ReceiptPrintOut";
import ErfInvoiceModal from "./ErfInvoiceModal";
import ReturnProcessModel from "./ReturnProcessModel";
import { isRentableErfType } from "./ClosureReturnSection";
import ERFExtensionAssignSEModal from "./ERFExtensionAssignSEModal";
import ERFExtensionJobDoneModal from "./ERFExtensionJobDoneModal";
import CreateServiceModal from "./CreateServiceModal";
import ViewServiceDetailsModal from "./ViewServiceDetailsModal";
import { SERVICE_ACTION_CALL_STATUS } from "./ServiceConstants";

const BOOLEAN_FILTER_KEYS = ["refundAmountIndicator", "creditNoteIndicator"];
const EXACT_MATCH_KEYS = [
  "pcCallStatus",
  "erfType",
  "privilegeCase",
  "pcdStatus",
  "serviceAction",
];

const ExtensionListERF = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // --- 1. CONFIG & STATE ---
  localStorage.setItem("menu", "Equipment Rent");
  localStorage.setItem("submenu", "Extented List");

  const userDataFromRedux = useSelector((state) => state?.user);

  // Select Data from Redux Store
  // We use optional chaining (?.) and default values (|| {}) to prevent crashes if state is loading
  // eslint-disable-next-line
  const { data: reduxData, pagination } =
    useSelector((state) => state?.customer?.equipmentRentExtForm) || {};

  const currentUserId = userDataFromRedux?.data?.userId || "M000";

  // Local State for Table UI
  // eslint-disable-next-line
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(100);
  // eslint-disable-next-line
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTxt, setSearchTxt] = useState("");
  const [isCardVisible, setCardVisible] = useState(true);
  const [filterShow, setFilterShow] = useState(false);

  const hasPCDExtAccess = userDataFromRedux?.data?.roles?.some(
    (r) => r.roleId === "SALES-PCD" || r.roleId === "SALES-ADMIN",
  );

  const [activeTab, setActiveTab] = useState("allExtension"); // "allExtension" | "pcdViewForExtension"
  const [activeFilters, setActiveFilters] = useState([]);

  /* Cancel Modal */
  const [getCancelModalStatus, setCancelModalStatus] = useState(false);
  // const cancelModalShow = () => {
  //   setCancelModalStatus(true);
  // };
  const cancelModalHide = () => {
    setCancelModalStatus(false);
  };

  /* Handle selected rows */
  const [getSelectedRows, setSelectedRows] = useState([]);

  const [jobDoneModal, setJobDoneModal] = useState({ show: false, row: null });
  const [assignSEModal, setAssignSEModal] = useState({
    show: false,
    row: null,
    viewOnly: false,
  });

  const [showCreateServiceModal, setShowCreateServiceModal] = useState(false);
  const [selectedErfForService, setSelectedErfForService] = useState(null);
  const [showViewServiceModal, setShowViewServiceModal] = useState(false);
  const [viewServiceData, setViewServiceData] = useState(null);

  // ── Role detection ────────────────────────────────────────────────────────
  const isServiceExecutive =
    userDataFromRedux?.data?.roles?.some(
      (r) => r.roleId === "SERVICE-EXECUTIVE",
    ) ?? false;

  const isDispatchManager =
    userDataFromRedux?.data?.roles?.some((r) => r.roleId === "DISPATCH") ??
    false;

  // eslint-disable-next-line
  const isFinanceExecutive =
    userDataFromRedux?.data?.roles?.some((r) => r.roleId === "FI-EXECUTIVE") ??
    false;

  const handleRowSelected = ({ selectedRows }) => {
    const validRows = selectedRows.filter(
      (item) => item?.statusHeader !== "Cancelled",
    );
    setSelectedRows(validRows);
  };

  const handleCreateService = (row) => {
    setSelectedErfForService(row);
    setShowCreateServiceModal(true);
  };

  const handleViewServiceDetails = (row) => {
    setViewServiceData(row);
    setShowViewServiceModal(true);
  };

  const handleServiceCreated = () => {
    setShowCreateServiceModal(false);
    setSelectedErfForService(null);
    fetchData(1, perPage, searchTxt, activeFilters, activeTab);
  };

  const normalizeFilters = (filters) =>
    filters.map((condition) => {
      const key = Object.keys(condition)[0];

      // Boolean fields → convert regex string to true / false
      if (BOOLEAN_FILTER_KEYS.includes(key)) {
        const regexStr = condition[key]?.$regex || "";
        const rawValue = regexStr.replace(/\.\*/g, "").toLowerCase();
        if (rawValue === "true" || rawValue === "pending")
          return { [key]: true };
        if (rawValue === "false" || rawValue === "done")
          return { [key]: false };
        return condition;
      }

      // Exact-match fields → strip regex wrapper, send plain string value
      if (EXACT_MATCH_KEYS.includes(key)) {
        const regexStr = condition[key]?.$regex || "";
        if (regexStr) {
          const rawValue = regexStr.replace(/\.\*/g, "").trim();
          return { [key]: rawValue };
        }
        return condition;
      }

      return condition;
    });

  // --- 2. FETCH DATA ---
  // const fetchData = async (page = 1, size = 100, search = "") => {
  //   setLoading(true);
  //   try {
  //     const payload = {
  //       queryName: "ExtEquipmentList",
  //       page: page,
  //       per_page: size,
  //       searchTerm: search,
  //       timeZone: "Asia/Calcutta",
  //       rowFilter: {},
  //     };

  //     if (isServiceExecutive) {
  //       payload.rowFilter = {
  //         serviceProviderCode: currentUserId,
  //       };
  //     }

  //     await dispatch(getEquipmentRentExtist(payload));
  //   } catch (error) {
  //     console.error("Error fetching list", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const fetchData = async (
    page = 1,
    size = 100,
    search = "",
    filters = [],
    tab = activeTab,
  ) => {
    setLoading(true);
    try {
      const queryName =
        tab === "pcdViewForExtension" ? "PCD_EXT_List" : "ExtEquipmentList";
      const filterConditions = [];

      if (isServiceExecutive) {
        filterConditions.push({ serviceProviderCode: currentUserId });
      }
      if (filters.length > 0) {
        filterConditions.push(...filters);
      }

      const payload = {
        queryName,
        page,
        per_page: size,
        searchTerm: search,
        timeZone: "Asia/Calcutta",
        rowFilter:
          filterConditions.length > 0 ? { $and: filterConditions } : {},
      };

      await dispatch(getEquipmentRentExtist(payload));
    } catch (error) {
      console.error("Error fetching list", error);
    } finally {
      setLoading(false);
    }
  };

  // // Initial Load & Pagination Trigger
  // useEffect(() => {
  //   fetchData(currentPage, perPage, searchTxt);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [currentPage, perPage]);

  // useEffect(() => {
  //   fetchData(1, perPage, searchTxt);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);
  useEffect(() => {
    if (!userDataFromRedux) return;
    fetchData(currentPage, perPage, searchTxt, activeFilters, activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDataFromRedux, currentPage, perPage, activeFilters, activeTab]);
  // useEffect(() => {
  //   if (pagination?.total && pagination.total > (reduxData?.length || 0)) {
  //     fetchData(1, pagination.total, searchTxt);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [pagination?.total]);

  const detailedPage = (row) => {
    // For documentNumber column
    dispatch(clearEquipmentRentNumbers());

    dispatch(setEquipmentRentExtDocumentNumber(row.documentNumber));
    // navigate(CustomerConstants.CUSTOMER_V5_MR_EXTENSION_ERF_DETAILS);
    navigate(CustomerConstants.CUSTOMER_V5_MR_EXTENSION_ERF_DETAILS);
  };

  const detailedPageforRent = (row) => {
    // For equipmentNr column
    dispatch(clearEquipmentRentNumbers());

    dispatch(setEquipmentRentExtDocumentNumber(row.equipmentNr)); //  FIXED
    navigate(CustomerConstants.CUSTOMER_V5_MR_ERF_SUMMARY_DETAILS);
  };

  // eslint-disable-next-line
  const handleCreate = () => {
    navigate(CustomerConstants.CUSTOMER_V5_MR_CREATE_ERF);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to page 1 on size change
  };

  const showStatusInfo = (rowData) => {
    setModalShowStatus(true);
    setSendDataModal(rowData);
    setModelTitle("Status Information");
  };

  const hideModalStatus = () => {
    setModalShowStatus(false);
  };

  const [showReturnProcessModel, setShowReturnProcessModel] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const returnProcessModel = (row) => {
    setSelectedRow(row);
    setShowReturnProcessModel(true);
  };

  const [modalShowStatus, setModalShowStatus] = useState(false);
  const [sendDataModal, setSendDataModal] = useState("");
  const [modelTitle, setModelTitle] = useState("");
  let userDatas = useSelector((state) => state?.user); // User Details from Store
  const [invoiceUserRoleData, serUserRoleData] = useState();

  const isEarlyClosureStatus = (status) => {
    return status === "Early Closure" || status === "Partial Early Closure";
  };

  //  Filter configuration for Gate Entry
  const filterColumns = [
    {
      id: "erfType",
      name: "ERF Type",
      selectorKey: "erfType",
      dropDown: true,
      staticOptions: ["Rent", "Sale & Rent", "Nursing Service"],
    },
    {
      id: "privilegeCase",
      name: "Privilege Case",
      selectorKey: "privilegeCase",
      dropDown: true,
      staticOptions: ["NOT APPLICABLE", "SPECIAL CASE", "DISCOUNT", "GIFT"],
    },
    // {
    //   id: "approvedBy",
    //   name: "Approved By",
    //   selectorKey: "approvedBy",
    //   dropDown: true,
    // },
    // {
    //   id: "hospital",
    //   name: "Hospital",
    //   selectorKey: "hospital",
    //   dropDown: true,
    // },
    {
      id: "salePoint",
      name: "Sale Point",
      selectorKey: "salePoint",
      range: true,
    },
    {
      id: "pcCallStatus",
      name: "PC Call Status",
      selectorKey: "pcCallStatus",
      dropDown: true,
      staticOptions: [
        "Extension",
        "Partial Ext",
        "Closed",
        "Follow Up",
        "Early Closure",
        "Partial Early Closure",
      ],
    },
    {
      id: "pcdStatus",
      name: "PCD Ext Status",
      selectorKey: "pcdStatus",
      dropDown: true,
      staticOptions: [
        "Expired",
        "Expires Today",
        "1 day left",
        "2 days left",
        "3 days left",
        "Valid",
      ],
    },
    {
      id: "documentNumber",
      name: "ERF Number",
      selectorKey: "documentNumber",
    },
    {
      id: "createdAt",
      name: "Created Date",
      selectorKey: "createdAt",
      month: true,
    },
    {
      id: "billingAddress",
      name: "Patient Address",
      selectorKey: "billingAddress",
    },
    {
      id: "mobileNr",
      name: "Mobile Number",
      selectorKey: "mobileNr",
    },
    {
      id: "patientName",
      name: "Patient Name",
      selectorKey: "patientName",
    },
    {
      id: "refundAmountIndicator",
      name: "Refund Amount Status",
      selectorKey: "refundAmountIndicator",
      dropDown: true,
      staticOptions: ["Done", "Pending"],
    },
    {
      id: "creditNoteIndicator",
      name: "Credit Note Status",
      selectorKey: "creditNoteIndicator",
      dropDown: true,
      staticOptions: ["Done", "Pending"],
    },

    {
      id: "tallyStatusIndicator",
      name: "Tally Status",
      selectorKey: "tallyStatusIndicator",
      dropDown: true,
      staticOptions: ["Done", "Pending"],
    },
    {
      id: "financeCollectionConfirmation",
      name: "Collection By Finance",
      selectorKey: "financeCollectionConfirmation",
      dropDown: true,
      staticOptions: ["Confirmed", "Not Confirmed"],
    },
    {
      id: "jobDoneStatus",
      name: "Job Status",
      selectorKey: "jobDoneStatus",
      dropDown: true,
      staticOptions: ["Done", "Not Done"],
    },
    {
      id: "serviceAction",
      name: "Service Action",
      selectorKey: "serviceAction",
      dropDown: true,
      staticOptions: [
        "Service",
        "Replacement",
        "Service Assigned",
        "Assign To Store",
        "Service In Progress",
        "Service Completed",
        "Returning To Store",
        "Service Closed",
        "Service Cancelled",
      ],
    },
  ];

  //  Handle multi-column filter
  // const handleMultiColumnFilter = async (filterResult) => {
  //   setLoading(true);
  //   try {
  //     const filtersArray = Array.isArray(filterResult)
  //       ? filterResult
  //       : filterResult?.$and || [];

  //     const normalized = normalizeFilters(filtersArray);

  //     const obj = {
  //       queryName: "ExtEquipmentList",
  //       page: 1,
  //       per_page: perPage,
  //       searchTerm: searchTxt,
  //       timeZone: "Asia/Calcutta",
  //       rowFilter: { $and: [] },
  //     };

  //     if (isServiceExecutive) {
  //       obj.rowFilter.$and.push({ serviceProviderCode: currentUserId });
  //     }

  //     if (normalized.length > 0) {
  //       obj.rowFilter.$and.push(...normalized);
  //     }

  //     if (obj.rowFilter.$and.length === 0) {
  //       delete obj.rowFilter;
  //     }

  //     await dispatch(getEquipmentRentExtist(obj));
  //   } catch (error) {
  //     console.error("Error fetching list", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // REPLACE the entire handleMultiColumnFilter with this:
  const handleMultiColumnFilter = (filterResult) => {
    const filtersArray = Array.isArray(filterResult)
      ? filterResult
      : filterResult?.$and || [];
    const normalized = normalizeFilters(filtersArray);
    setActiveFilters(normalized);
    setCurrentPage(1);
  };

  // //  Handle filter cancel
  // const handleFilterCancel = () => {
  //   setFilterShow(false);
  // };

  const handleFilterCancel = () => {
    setFilterShow(false);
    setActiveFilters([]);
    setCurrentPage(1);
  };
  //print
  //po print invocie
  const contentRef = useRef(null);
  const [getLoadingAdvice, setLoadingAdvice] = useState([]);
  // useReactToPrint declared at top level
  const handlePrint = useReactToPrint({
    contentRef,
    // documentTitle: `loadingAdvice_${getLoadingAdvice?.documentNumber || ""}`,
  });
  const handlePrintWithData = (row) => {
    setLoadingAdvice(row);

    // Wait for state updates before printing
    setTimeout(() => {
      handlePrint();
    }, 500);
  };

  // --- 4. COLUMNS DEFINITION (Fixed keys to match API) ---
  // eslint-disable-next-line
  const additionalColumns = [
    {
      name: "Feedback Form",
      selector: (row) => row.feedbackForm,
      sortable: true,
      width: "160px",
    },
    {
      name: "Closure Feedback Form",
      selector: (row) => row.closureFeedbackForm,
      sortable: true,
      width: "190px",
    },
    {
      name: "Closure Feedback Status",
      selector: (row) => row.closureFeedbackStatus,
      sortable: true,
      width: "200px",
    },
    {
      name: "Patient No",
      selector: (row) => row.contactNumber,
      width: "140px",
    },
    // {
    //   name: "Approved By",
    //   selector: (row) => row.approvedBy,
    //   width: "150px",
    // },
    {
      name: "Approval Status",
      selector: (row) => row.status,
      cell: (row) => <TableStatusField status={row.status} />,
      width: "150px",
    },
    {
      name: "Tally Status",
      selector: (row) => row.tallyStatus,
      width: "140px",
    },
    {
      name: "PB Time",
      selector: (row) => row.pbTime,
      width: "120px",
    },
    {
      name: "MD Status",
      selector: (row) => row.mdStatus,
      width: "140px",
    },
    {
      name: "MD Time",
      selector: (row) => row.mdTime,
      width: "120px",
    },
    {
      name: "Sale Type",
      selector: (row) => row.saleType,
      width: "130px",
    },
    {
      name: "Caregiver No",
      selector: (row) => row.caregiverNo,
      width: "150px",
    },
    {
      name: "Cost Centre",
      selector: (row) => row.costCentre,
      width: "150px",
    },
    {
      name: "PC Status",
      selector: (row) => row.pcStatus,
      width: "140px",
    },
    {
      name: "Order Type",
      selector: (row) => row.orderType,
      width: "140px",
    },
    {
      name: "Relationship",
      selector: (row) => row.relationship,
      width: "150px",
    },
    {
      name: "Challenges",
      selector: (row) => row.challenges,
      wrap: true,
      width: "220px",
    },
  ];
  const userROle = JSON.parse(localStorage.getItem("userData"));
  const hasDIspatchRole = userROle?.data?.roles?.some(
    (r) => r.roleId === "DISPATCH",
  );
  const userStore = JSON.parse(localStorage.getItem("userData"));
  const hasstoreRole = userStore?.data?.roles?.some(
    (r) => r.roleId === "STORE",
  );
  // eslint-disable-next-line
  const shouldHideColumns = hasDIspatchRole || hasstoreRole;

  //print for earlyclosure
  const contentRef1 = useRef(null);
  const [getEarlyClosure, setEarlyClosure] = useState(null);

  const earlyClosureHandlePrint = useReactToPrint({
    contentRef: contentRef1,
  });

  const handleEarlyClosurePrintWithData = (row) => {
    setEarlyClosure(row);
    setTimeout(() => {
      earlyClosureHandlePrint();
    }, 300);
  };

  useEffect(() => {
    if (getEarlyClosure) {
      earlyClosureHandlePrint();
    }
    // eslint-disable-next-line
  }, [getEarlyClosure]);

  //print for closureRefundForm
  const contentRef2 = useRef(null);
  const [getClosureRefundForm, setClosureRefundForm] = useState(null);

  const closureRefundFormHandlePrint = useReactToPrint({
    contentRef: contentRef2,
  });

  const handleClosureRefundFormPrintWithData = (row) => {
    setClosureRefundForm(row);
    setTimeout(() => {
      closureRefundFormHandlePrint();
    }, 300);
  };

  useEffect(() => {
    if (getClosureRefundForm) {
      closureRefundFormHandlePrint();
    }
    // eslint-disable-next-line
  }, [getClosureRefundForm]);

  //print for deliveryChallan
  const contentRef3 = useRef(null);
  const [getdeliveryChallan, setdeliveryChallan] = useState(null);

  const deliveryChallanHandlePrint = useReactToPrint({
    contentRef: contentRef3,
  });

  const handledeliveryChallanPrintWithData = (row) => {
    setdeliveryChallan(row);
    setTimeout(() => {
      deliveryChallanHandlePrint();
    }, 300);
  };

  useEffect(() => {
    if (getdeliveryChallan) {
      deliveryChallanHandlePrint();
    }
    // eslint-disable-next-line
  }, [getdeliveryChallan]);

  //print for Receipt
  const contentRef4 = useRef(null);
  const [getReceipt, setReceipt] = useState(null);
  const cartage = true;
  const ReceiptHandlePrint = useReactToPrint({
    contentRef: contentRef4,
  });

  const handleReceiptPrintWithData = (row) => {
    setReceipt(row);
    setTimeout(() => {
      ReceiptHandlePrint();
    }, 300);
  };

  useEffect(() => {
    if (getReceipt) {
      ReceiptHandlePrint();
    }
    // eslint-disable-next-line
  }, [getReceipt]);

  const columns = [
    {
      name: "Created Date",
      selector: (row) => row.orderDate, // MATCHED API KEY
      cell: (row) => (
        <div>
          <span className="fw-medium">{formatDateWithAMPM(row.createdAt)}</span>
        </div>
      ),
      sortable: true,
      width: "190px",
    },

    {
      name: "Equipment RE Nr",
      selector: (row) => row.documentNumber,
      cell: (row) => (
        <button className="btnInfo" onClick={() => detailedPage(row)}>
          {row.documentNumber || "N/A"}
        </button>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Erf Nr",
      selector: (row) => row.equipmentNr,
      cell: (row) => (
        <button className="btnInfo" onClick={() => detailedPageforRent(row)}>
          {row.equipmentNr || "N/A"}
        </button>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Invoice",
      cell: (row) => {
        const enableGenerate =
          row?.financeCollectionConfirmation === "Confirmed";

        return row?.extInvoiceCreated ? (
          <i
            className={`
bi bi-receipt 
text-[26px] 
text-[#2d7a79] 
transition-colors duration-300
cursor-pointer hover:text-[#4aa3a2]
`}
            onClick={() => showEditInvoice(row)}
          ></i>
        ) : (
          <button
            className="btn btnTable"
            disabled={!enableGenerate}
            onClick={() => showEditInvoice(row)}
          >
            Generate
          </button>
        );
      },
      width: "150px",
      // center: true,
      style: { justifyContent: "center" },
      omit: !invoiceUserRoleData,
    },

    {
      name: "ERF For",
      selector: (row) => row.erfForType,
      cell: (row) => (
        // <span className="fw-bold text-primary">{row.erfForType}</span>
        <span
          // className="badge"
          style={{
            color: row.erfForType === "Hospital" ? "red" : "green",
            fontWeight: "bold",
          }}
        >
          {row.erfForType}
        </span>
      ),
      sortable: true,
      width: "120px",
    },

    {
      name: "ERF Type & Privilege Case ",
      selector: (row) => row.erfType,
      cell: (row) => (
        <div>
          <span>
            <TableStatusField status={row.erfType} />
          </span>
          <p className="fw-bold mt-1">{row.privilegeCase}</p>
        </div>
      ),
      sortable: true,
      width: "210px",
    },
    {
      name: "PC Call Status",
      id: "pcCallStatus",
      dropDown: true,
      selector: (row) => row.pcCallStatus, // MATCHED API KEY
      cell: (row) => <TableStatusField status={row.pcCallStatus} />,
      width: "150px",
    },
    {
      name:
        activeTab === "pcdViewForExtension" ? "PCD Ext Status" : "Ending Date",
      cell: (row) => {
        if (activeTab === "pcdViewForExtension") {
          return (
            <div>
              <TableStatusField status={row?.pcdStatus} />
              <small className="text-muted d-block mt-1">
                {formatDate(row?.endingDate)}
              </small>
            </div>
          );
        }
        return row?.endingDate ? (
          <small className="text-muted">{formatDate(row?.endingDate)}</small>
        ) : (
          <span>-</span>
        );
      },
      width: "160px",
    },
    {
      name: "Return Process",
      cell: (row) => {
        if (!isRentableErfType(row?.erfTypeCode)) {
          return <span className="text-muted small">--</span>;
        }

        const isPartialExt =
          row.pcCallStatus === "Partial Ext" || row.pcCallStatusCode === "PEXT";
        const isClosed =
          row.pcCallStatus === "Closed" || row.pcCallStatusCode === "CD";
        const isEarlyClosure =
          row?.pcCallStatus === "Early Closure" ||
          row.pcCallStatusCode === "ELCS";
        const isPartialEarlyClosure =
          row?.pcCallStatus === "Partial Early Closure" ||
          row.pcCallStatusCode === "PELCS";

        if (
          isPartialExt ||
          isClosed ||
          isEarlyClosure ||
          isPartialEarlyClosure
        ) {
          return (
            <button className="btnInfo" onClick={() => returnProcessModel(row)}>
              <i className="bi bi-arrow-counterclockwise me-1"></i>
              Return Process
            </button>
          );
        }
        return <span className="text-muted small">--</span>;
      },
      sortable: true,
      // center: true,
      style: { justifyContent: "center" },
      width: "180px",
    },

    ...(reduxData?.some(
      (row) =>
        Array.isArray(row?.workflowActionLogs) &&
        row.workflowActionLogs.length > 0,
    )
      ? [
          {
            name: "Workflow log",
            selector: (row) => row.workflowActionLogs,
            cell: (row) => {
              const hasLogs =
                Array.isArray(row?.workflowActionLogs) &&
                row.workflowActionLogs.length > 0;

              return (
                <div
                  style={{ width: "100%" }}
                  className="d-flex justify-content-center iconTable"
                >
                  <img
                    src="assets/img/newIcons/workflow.png"
                    alt="workflow"
                    style={{
                      width: "35px",
                      opacity: hasLogs ? 1 : 0.4,
                      cursor: hasLogs ? "pointer" : "not-allowed",
                    }}
                    className="mt-1"
                    onClick={
                      hasLogs
                        ? () => showStatusInfo(row.workflowActionLogs)
                        : undefined
                    }
                  />
                </div>
              );
            },
            sortable: true,
            width: "132px",
          },
        ]
      : []),
    {
      name: "ERF Status",
      selector: (row) => row.erfStatus,
      cell: (row) => (
        <span>
          <TableStatusField status={row.erfStatus} />
        </span>
      ),
      width: "240px",
      style: { justifyContent: "center" },
    },
    {
      name: "SE Assignment",
      width: "190px",
      cell: (row) => {
        // ── Dispatch Manager: assign SE when status is PAYPENDING ─────────────
        if (isDispatchManager && row.erfStatusCode === "PAYPENDING") {
          return (
            <button
              className="btnTable"
              style={{ fontSize: "12px", padding: "4px 8px" }}
              onClick={() =>
                setAssignSEModal({ show: true, row, viewOnly: false })
              }
            >
              <i className="bi bi-person-plus me-1" />
              Assign SE
            </button>
          );
        }

        // ── Anyone: view SE history when REUSE or REPAYCOLLECTED ─────────────
        if (
          row.erfStatusCode === "REUSE" ||
          row.erfStatusCode === "REPAYCOLLECTED"
        ) {
          return (
            <button
              className="btnTable"
              style={{ fontSize: "12px", padding: "4px 8px", opacity: 0.85 }}
              onClick={() =>
                setAssignSEModal({ show: true, row, viewOnly: true })
              }
            >
              <i className="bi bi-person-lines-fill me-1" />
              View SE
            </button>
          );
        }

        return <span className="text-muted small">—</span>;
      },
    },
    {
      name: "Payment",
      width: "190px",
      cell: (row) => {
        // ── Service Executive: collect payment when REUSE (own record only) ───
        if (isServiceExecutive && row.erfStatusCode === "REUSE") {
          const myUserId = userDataFromRedux?.data?.userId;
          if (row.serviceProviderUserId !== myUserId) {
            return <span className="text-muted small">—</span>;
          }
          return (
            <button
              className="btnTable"
              style={{ fontSize: "12px", padding: "4px 8px" }}
              onClick={() => setJobDoneModal({ show: true, row })}
            >
              <i className="bi bi-cash-coin me-1" />
              Process Payment
            </button>
          );
        }

        // ── View payment when already collected ───────────────────────────────
        if (row.erfStatusCode === "REPAYCOLLECTED") {
          return (
            <button
              className="btnTable"
              style={{ fontSize: "12px", padding: "4px 8px", opacity: 0.85 }}
              onClick={() => setJobDoneModal({ show: true, row })}
            >
              <i className="bi bi-receipt-cutoff me-1" />
              Payment Info
            </button>
          );
        }

        return <span className="text-muted small">—</span>;
      },
    },
    {
      name: "Tally Status",
      selector: (row) => row?.tallyStatusIndicator,
      cell: (row) => (
        <span
          style={{
            fontWeight: "bold",
            color:
              row.tallyStatusIndicator === "Done"
                ? "#2563eb"
                : row.tallyStatusIndicator === "Pending"
                  ? "#dc2626"
                  : "#6b7280",
          }}
        >
          {row.tallyStatusIndicator || "-"}
        </span>
      ),
      wrap: true,
      width: "120px",
    },
    {
      name: "Collect. By Finance",
      selector: (row) => row?.financeCollectionConfirmation,
      cell: (row) => (
        <span
          style={{
            fontWeight: "bold",
            color:
              row?.financeCollectionConfirmation === "Confirmed"
                ? "#2563eb"
                : row?.financeCollectionConfirmation === "Not Confirmed"
                  ? "#dc2626"
                  : "#6b7280",
          }}
        >
          {row?.financeCollectionConfirmation || "-"}
        </span>
      ),
      wrap: true,
      width: "180px",
    },
    {
      name: "Refund Status",
      selector: (row) => row.refundAmountIndicator,
      cell: (row) => (
        <span
          style={{
            fontWeight: "bold",
            color:
              row.refundAmountIndicator === true
                ? "orange"
                : row.refundAmountIndicator === false
                  ? "blue"
                  : "#6b7280",
          }}
        >
          {row.refundAmountIndicator === false
            ? "Done"
            : row.refundAmountIndicator === true
              ? "Pending"
              : "-"}
        </span>
      ),
      wrap: true,
      width: "155px",
    },

    {
      name: "Job Status",
      cell: (row) => {
        const status = row?.jobDoneStatus;
        if (!status) return <span className="text-muted">--</span>;
        const color =
          status === "Done"
            ? "#28a745"
            : status === "Not Done"
              ? "#dc3545"
              : "#ffc107";
        return (
          <span
            className="badge"
            style={{
              backgroundColor: color,
              color: status === "Pending" ? "black" : "white",
              padding: "6px 10px",
              borderRadius: "10px",
            }}
          >
            {status}
          </span>
        );
      },
      width: "140px",
    },
    {
      name: "Patient Details",
      selector: (row) => row.patientName,
      cell: (row) => (
        <div>
          <p className="fw-bold">{row?.patientName || "--"}</p>
          <p>{row?.mobileNr || "--"}</p>
          <p>{row?.billingAddress || "--"}</p>
        </div>
      ),
      sortable: true,
      width: "160px",
    },
    {
      name: "Amount",
      selector: (row) => row?.netAmount || 0,
      cell: (row) => (
        <div
          className="fw fw-bold"
          style={{
            color: "blue",
            fontWeight: "bold",
          }}
        >
          {`${Number(row?.netAmount || 0).toFixed(2)} INR`}
        </div>
      ),
      // right: true,
      width: "130px",
    },
    {
      name: "Sale Point",
      selector: (row) => row.salePoint,
      cell: (row) => (
        <div>
          <p>{row.salePoint}</p>
        </div>
      ),
      sortable: true,
      wrap: true,
      width: "120px",
    },

    {
      name: "Created By",
      selector: (row) => row.createdBy,
      cell: (row) => (
        <div>
          <p className="fw fw-bold">{row.createdBy}</p>
        </div>
      ),
      sortable: true,
      wrap: true,
      width: "120px",
    },
    {
      name: "Updated Remarks",
      cell: (row) => {
        const version = row?.version || "V0";
        const count = row?.updateRemarks?.length || 0;

        return (
          <>
            <span
              style={{ marginLeft: "12px" }}
              onClick={() => remarkItemShowModal(row?.updateRemarks || [])}
            >
              <i className="bi bi-calendar2-week iconTable"></i>
            </span>

            <span className="suppliersIcon">{`${count}/${version}`}</span>
          </>
        );
      },
      width: "200px",
    },
    {
      name: "Service Action",
      id: "serviceAction",
      selector: (row) => row.serviceAction,
      cell: (row) =>
        row?.serviceAction ? (
          <TableStatusField status={row.serviceAction} />
        ) : (
          <span className="text-muted small">—</span>
        ),
      width: "170px",
    },
    ...(isDispatchManager
      ? [
          {
            name: "Create Service",
            cell: (row) => (
              <>
                {[
                  "Service",
                  "Replacement",
                  "Service Completed",
                  "Service Cancelled",
                ].includes(row?.serviceAction) && (
                  <button
                    className="btnInfo"
                    onClick={() => handleCreateService(row)}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Create Service
                  </button>
                )}
              </>
            ),
            width: "160px",
          },
        ]
      : []),
    {
      name: "View Updated Service",
      cell: (row) => {
        const activeStatuses = Object.values(SERVICE_ACTION_CALL_STATUS);
        const hasServiceRecord =
          activeStatuses.includes(row?.serviceAction) || !!row?.latestServiceNr;
        if (!hasServiceRecord)
          return <span className="text-muted small">—</span>;
        return (
          <button
            className="btnInfo"
            onClick={() => handleViewServiceDetails(row)}
          >
            <i className="bi bi-eye me-1" /> View Details
          </button>
        );
      },
      width: "175px",
    },

    {
      name: "Receipt",
      selector: (row) => row.attachments,
      cell: (row) => (
        <div
          onClick={() => handleReceiptPrintWithData(row)}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
        >
          <i className="bi bi-printer text-lg fs-5"></i>
        </div>
      ),
      sortable: true,
      width: "130px",
    },
    {
      name: "Agremnent",
      selector: (row) => row.attachments,
      cell: (row) => (
        <div
          onClick={() => handlePrintWithData(row)}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
        >
          <i className="bi bi-printer text-lg fs-5"></i>
        </div>
      ),
      sortable: true,
      width: "130px",
    },

    {
      name: "Early Closure Form",
      selector: (row) => row.attachments,
      cell: (row) =>
        isEarlyClosureStatus(row.pcCallStatus) ? (
          <div
            onClick={() => handleEarlyClosurePrintWithData(row)}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
          >
            <i className="bi bi-printer text-lg fs-5"></i>
          </div>
        ) : (
          "--" //  hide when not matching
        ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Closure Refund Form",
      selector: (row) => row.attachments,
      cell: (row) =>
        isEarlyClosureStatus(row.pcCallStatus) ? (
          <div
            onClick={() => handleClosureRefundFormPrintWithData(row)}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
          >
            <i className="bi bi-printer text-lg fs-5"></i>
          </div>
        ) : (
          "--" //  hide when not matching
        ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Delivery Challan",
      selector: (row) => row.attachments,
      cell: (row) => (
        <div
          onClick={() => handledeliveryChallanPrintWithData(row)}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
        >
          <i className="bi bi-printer text-lg fs-5"></i>
        </div>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Credit Note Status",
      selector: (row) => row.creditNoteIndicator,
      cell: (row) => (
        <span
          style={{
            fontWeight: "bold",
            color:
              row.creditNoteIndicator === true
                ? "red"
                : row.creditNoteIndicator === false
                  ? "green"
                  : "#6b7280",
          }}
        >
          {row.creditNoteIndicator === false
            ? "Done"
            : row.creditNoteIndicator === true
              ? "Pending"
              : "-"}
        </span>
      ),
      wrap: true,
      width: "160px",
    },
    // ...additionalColumns,
    // ...(!shouldHideColumns
    //   ? [

    //     ]
    //   : []),
  ];

  const [getItemLevelData, setItemLevelData] = useState([]);
  const [getItemLevelColumns, setItemLevelColumns] = useState([]);
  const [getModalTitle, setModalTitle] = useState("");
  const remarkLineItemColInfo = [
    {
      name: "Remarks",
      cell: (row) => <span>{row?.remark || ""} </span>,
      width: "180px",
    },
    {
      name: "Version",
      cell: (row) => <span>{row?.version || ""} </span>,
      width: "180px",
    },
  ];
  const [itemLevelModalShow, setItemLevelModalShow] = useState(false);

  const remarkItemShowModal = (itemLevelData) => {
    setItemLevelData(itemLevelData);
    setItemLevelColumns(remarkLineItemColInfo);
    setModalTitle("ERF Remarks / Version");
    setItemLevelModalShow(true);
  };
  const itemLevelHideModal = () => {
    setItemLevelModalShow(false);
  };

  const userData = JSON.parse(localStorage.getItem("userData"));
  // eslint-disable-next-line
  const hasPCDRole = userData?.data?.roles?.some(
    (r) => r.roleId === "SALES-PCD",
  );

  useEffect(() => {
    if (userDatas?.data?.roles) {
      const allowedRoles = ["FI-EXECUTIVE"];

      const isAllowed = userDatas?.data?.roles?.some((r) =>
        allowedRoles.includes(r.roleId),
      );

      serUserRoleData(isAllowed);
    }
    // eslint-disable-next-line
  }, [userDatas]);

  const [getModalEditInvoice, setModalEditInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const showEditInvoice = (rowData) => {
    setModalEditInvoice(true);
    setInvoiceData(rowData);
  };
  const hideInvoiceModal = () => {
    setModalEditInvoice(false);
    setInvoiceData(null);
    // setEwayBillData(null);
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            <Row>
              <Col xs={12} md={8} lg={8}>
                <h2 className="mainTitle">ERF Extended List - RE</h2>
                <p className="subText">
                  Review and monitor extended rental agreements
                </p>
              </Col>

              <Col xs={12} md={3} lg={3} className="mt-3">
                <SearchField
                  name="searchKey"
                  value={searchTxt}
                  onChange={(e) => setSearchTxt(e.target.value)}
                  // onClick={() => fetchData(1, perPage, searchTxt)}
                  onClick={() =>
                    fetchData(1, perPage, searchTxt, activeFilters, activeTab)
                  }
                  placeholder="Search..."
                />
              </Col>

              <Col xs={4} md={1} lg={1}>
                <span
                  className="filterIcon"
                  onClick={() => setFilterShow(!filterShow)}
                >
                  <i className="bi bi-filter"></i>
                </span>
              </Col>
            </Row>
            <hr />
            {hasPCDExtAccess && (
              <Row className="mb-3">
                <Col xs={12} md={6} lg={6} className="pe-md-1 mb-1 mb-md-0">
                  <button
                    onClick={() => {
                      setLoading(true);
                      setActiveTab("allExtension");
                      setCurrentPage(1);
                      setActiveFilters([]);
                    }}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "6px",
                      fontWeight: "600",
                      cursor: "pointer",
                      fontSize: "14px",
                      backgroundColor:
                        activeTab === "allExtension" ? "#76B8B1" : "#ffffff",
                      border:
                        activeTab === "allExtension"
                          ? "1px solid #76B8B1"
                          : "1px solid #2d7a79",
                      color: "#000000",
                    }}
                  >
                    All Extensions
                  </button>
                </Col>
                <Col xs={12} md={6} lg={6} className="ps-md-1">
                  <button
                    onClick={() => {
                      setLoading(true);
                      setActiveTab("pcdViewForExtension");
                      setCurrentPage(1);
                      setActiveFilters([]);
                    }}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "6px",
                      fontWeight: "600",
                      cursor: "pointer",
                      fontSize: "14px",
                      backgroundColor:
                        activeTab === "pcdViewForExtension"
                          ? "#76B8B1"
                          : "#ffffff",
                      border:
                        activeTab === "pcdViewForExtension"
                          ? "1px solid #76B8B1"
                          : "1px solid #2d7a79",
                      color: "#000000",
                    }}
                  >
                    Extensions For RE
                  </button>
                </Col>
              </Row>
            )}
            <Row>
              <Col xs={12} md={12} lg={12}>
                {filterShow && (
                  <MultiColumnFilter
                    columns={filterColumns}
                    data={reduxData}
                    onFilter={handleMultiColumnFilter}
                    onCancel={handleFilterCancel}
                    output="and"
                  />
                )}
              </Col>
            </Row>

            {/* --- Table Accordion --- */}
            <div className="accordion" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible ? "bi-chevron-down" : "bi-chevron-right"
                        }`}
                        onClick={() => setCardVisible(!isCardVisible)}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">EL</span>
                    </Col>
                    <Col xs={6} md={4} lg={4}>
                      <span className="headerTitle">Extension Rental List</span>
                      <span className="subHeaderTitle">
                        View Details and Status
                      </span>
                    </Col>

                    {/* Create Button in Accordion Header */}

                    <Col xs={12} md={8} lg={8} className="headerButtonAlign">
                      {/* <Col xs={4} md={2} lg={2} className="ms-auto">
                        <button className="btnTable" onClick={handleCreate}>
                          <i className="bi bi-plus-lg mx-2"></i> Create ERF
                        </button>
                      </Col> */}
                      {/* {!hasPCDRole && (
                        <Col
                          xs={12}
                          md={8}
                          lg={8}
                          className="headerButtonAlign"
                        >
                          <Col xs={4} md={2} lg={2} className="ms-auto">
                            <button className="btnTable" onClick={handleCreate}>
                              <i className="bi bi-plus-lg mx-2"></i> Create ERF
                            </button>
                          </Col>
                        </Col>
                      )} */}
                    </Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <Row>
                      <Col xs={12}>
                        {loading ? (
                          <div className="text-center py-5">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="mt-3 text-muted">
                              {activeTab === "pcdViewForExtension"
                                ? "Loading PCD follow-up records..."
                                : "Loading extension list..."}
                            </p>
                          </div>
                        ) : (
                          <DataTable
                            title=""
                            columns={columns}
                            data={reduxData || []}
                            pagination
                            paginationServer
                            paginationPerPage={perPage}
                            // Pass total rows from Redux pagination
                            paginationTotalRows={pagination?.total || 0}
                            paginationRowsPerPageOptions={
                              CustomerConstants.ROW_REPORT_PER_PAGE
                            }
                            // Handlers
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            selectableRows
                            highlightOnHover
                            pointerOnHover
                            persistTableHead={false}
                            onSelectedRowsChange={handleRowSelected}
                          />
                        )}
                      </Col>
                    </Row>
                    <div style={{ display: "none" }}>
                      <ErfPrintout ref={contentRef} data={getLoadingAdvice} />
                    </div>
                    <div style={{ display: "none" }}>
                      <EarlyClosureFormPrintout
                        ref={contentRef1}
                        data={getEarlyClosure}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <ClosureRefundFormPrintout
                        ref={contentRef2}
                        data={getClosureRefundForm}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <DeliveryChallanPrintout
                        ref={contentRef3}
                        data={getdeliveryChallan}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <ReceiptPrintOut
                        key={
                          getReceipt?.documentNumber || "Receipt-Printout-key"
                        }
                        ref={contentRef4}
                        data={getReceipt}
                        cartage={cartage}
                        balanceSecurityAmt={true}
                        isExtension={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <CancelModal
        title="Cancel the ERF Form"
        show={getCancelModalStatus}
        onHide={cancelModalHide}
        selectedrow={getSelectedRows}
      />
      <ShowStatus
        show={modalShowStatus}
        onHide={() => hideModalStatus()}
        rowdatamodal={sendDataModal}
        modeltitle={modelTitle}
      />

      <CommonItemLevelDatatableModal
        show={itemLevelModalShow}
        onHide={() => itemLevelHideModal()}
        columns={getItemLevelColumns}
        data={getItemLevelData}
        title={getModalTitle}
      />
      <ErfInvoiceModal
        show={getModalEditInvoice}
        onHide={() => hideInvoiceModal()}
        invoiceinfo={invoiceData}
        // onsuccessdata={handleSuccessData}
        // viewInvoiceData={viewInvoiceData}
      />
      <ReturnProcessModel
        key={`return-${selectedRow?.documentNumber || "empty"}`}
        show={showReturnProcessModel}
        onHide={() => setShowReturnProcessModel(false)}
        // erfData={selectedRow}
        documentNumber={selectedRow?.documentNumber}
        isExtension={true}
      />

      {/* ── Extension Job Done / Payment Modal ─────────────────────────────── */}
      <ERFExtensionJobDoneModal
        key={jobDoneModal.row?.documentNumber ?? "job-done-modal"}
        show={jobDoneModal.show}
        onHide={() => setJobDoneModal({ show: false, row: null })}
        row={jobDoneModal.row}
        onSuccess={() => {
          setJobDoneModal({ show: false, row: null });
          // fetchData(1, perPage, searchTxt);
          fetchData(1, perPage, searchTxt, activeFilters, activeTab);
        }}
      />
      {/* ── Assign SE Modal (Dispatch Manager) ─────────────────────────────── */}
      <ERFExtensionAssignSEModal
        key={
          (assignSEModal.row?.documentNumber ?? "extension-assign-modal") +
          (assignSEModal.viewOnly ? "-view" : "-assign")
        }
        show={assignSEModal.show}
        onHide={() =>
          setAssignSEModal({ show: false, row: null, viewOnly: false })
        }
        erfData={assignSEModal.row}
        viewOnly={assignSEModal.viewOnly}
        onSuccess={() => {
          setAssignSEModal({ show: false, row: null, viewOnly: false });
          fetchData(1, perPage, searchTxt);
        }}
      />
      <CreateServiceModal
        key={`createService-${selectedErfForService?.documentNumber || "createService-empty"}`}
        show={showCreateServiceModal}
        onHide={() => setShowCreateServiceModal(false)}
        erfData={selectedErfForService}
        onSuccess={handleServiceCreated}
      />
      <ViewServiceDetailsModal
        key={`viewService-${viewServiceData?.documentNumber || "viewService-empty"}`}
        show={showViewServiceModal}
        onHide={() => setShowViewServiceModal(false)}
        data={viewServiceData}
      />
    </main>
  );
};

export default ExtensionListERF;
